import React from 'react';
import { Award, Download, ExternalLink, FileText, Loader2 } from 'lucide-react';

/**
 * Post-analysis primary-action bar with three concerns:
 *   1. "Analysis complete" banner with a `View Full Results` CTA.
 *   2. DOCX / XLSX / PDF export buttons.
 *   3. "Send to Write My Paper" handoff.
 *   4. Sub-Batch 3L (Feb 2026): "Decision Card PDF" CTA for Augmented Design only.
 *
 * Extracted from DesignUploadCard.js (P2 refactor, Feb 2026) to keep
 * the parent card under 700 LOC and improve maintainability.
 */
export function ExportBar({ analysisId, color, exporting, onExport, designKey, onConnectPaper }) {
  if (!analysisId) return null;
  // Resolve design accent colour with a sensible fallback so the
  // primary-action banner always matches the design it belongs to.
  const accent = color || '#2D6A4F';

  // Sub-Batch 3L/3M/3N/3O/3P (Feb 2026): the Breeder's Decision Card is
  // a synthesis artifact powered by BLUP + Consensus + ΔG outputs
  // (Sub-Batches 3I/3J/3K). Surface the CTA on any design that
  // produces these blocks — currently Augmented, RBD, Alpha-Lattice,
  // Split-Plot, and Strip-Plot. Future Sub-Batches will add Latin
  // Square, BIB, Nested designs.
  const lcDesign = (designKey || '').toLowerCase();
  const supportsDecisionCard = (
    lcDesign.includes('augment')
    || lcDesign === 'rbd'
    || lcDesign.includes('randomized')
    || lcDesign.includes('alpha')
    || lcDesign.includes('lattice')
    || lcDesign.includes('split')
    || lcDesign.includes('strip')
  );
  const handleDecisionCard = async () => {
    return _fetchAndOpenCard(
      `${process.env.REACT_APP_BACKEND_URL}/api/document/design-decision-card/${analysisId}.pdf`,
    );
  };
  const handleMultiTraitCard = async () => {
    return _fetchAndOpenCard(
      `${process.env.REACT_APP_BACKEND_URL}/api/document/design-decision-card/${analysisId}/all.pdf`,
    );
  };
  // Sub-Batch 3N (Feb 27 2026): 10-slide python-pptx committee presentation
  // deck.  Available only on Augmented designs (ABD/ACRD).  Reuses the same
  // fetch-and-open + sonner-toast pattern as the Decision Card CTAs.
  const supportsPresentationDeck = lcDesign.includes('augment');
  const handlePresentationDeck = async () => {
    return _fetchAndOpenFile(
      `${process.env.REACT_APP_BACKEND_URL}/api/document/presentation-deck/${analysisId}.pptx`,
      'Presentation Deck unavailable',
    );
  };
  // Generic fetch-and-open helper — same pattern as _fetchAndOpenCard but
  // parameterised so non-PDF artifacts (PPTX) reuse the same error UX.
  const _fetchAndOpenFile = async (url, errorTitle) => {
    try {
      const resp = await fetch(url, { credentials: 'include' });
      if (resp.ok) {
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        // PPTX files download rather than render inline; trigger via anchor.
        const a = document.createElement('a');
        a.href = objUrl;
        // Use the Content-Disposition filename if present, else generic.
        const cd = resp.headers.get('Content-Disposition') || '';
        const m = cd.match(/filename=([^;]+)/i);
        a.download = m ? m[1].trim() : `BKAgriD_Presentation_${analysisId.slice(0, 8)}.pptx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
      } else {
        let detail = `${errorTitle} (HTTP ${resp.status})`;
        try {
          const errBody = await resp.json();
          if (errBody && errBody.detail) detail = errBody.detail;
        } catch (_) { /* fall back to default */ }
        try {
          const { toast } = await import('sonner');
          toast.error(errorTitle, { description: detail, duration: 12000 });
        } catch (_) {
          window.alert(`${errorTitle}:\n\n${detail}`);
        }
      }
    } catch (err) {
      try {
        const { toast } = await import('sonner');
        toast.error('Network error', { description: String(err), duration: 8000 });
      } catch (_) {
        window.alert(`Network error: ${String(err)}`);
      }
    }
  };
  // Sub-Batch 3N+1 hardening (Feb 2026): instead of just window.open
  // (which renders raw JSON for a 400 error), fetch the PDF first,
  // toast a useful error if it fails, and only open if status=200.
  const _fetchAndOpenCard = async (url) => {
    try {
      const resp = await fetch(url, { credentials: 'include' });
      if (resp.ok) {
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        window.open(objUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
      } else {
        let detail = `Decision Card unavailable (HTTP ${resp.status})`;
        try {
          const errBody = await resp.json();
          if (errBody && errBody.detail) detail = errBody.detail;
        } catch (_) { /* fall back to default */ }
        try {
          const { toast } = await import('sonner');
          toast.error('Decision Card unavailable', { description: detail, duration: 12000 });
        } catch (_) {
          window.alert(`Decision Card unavailable:\n\n${detail}`);
        }
      }
    } catch (err) {
      try {
        const { toast } = await import('sonner');
        toast.error('Network error', { description: String(err), duration: 8000 });
      } catch (_) {
        window.alert(`Network error: ${String(err)}`);
      }
    }
  };
  return (
    <div className="flex flex-col gap-3 mt-5 pt-5" style={{ borderTop: '1px solid var(--border-color)' }} data-testid="export-bar">
      {/* Analysis-complete banner — the primary action right after a
          successful run is "See the full report". Making this visible
          as a prominent banner (instead of a subtle pill pushed to
          the right of the export row) ensures breeders don't miss
          their way to the dedicated Results page with tables,
          figures, and downloads. (Niranjan, Feb 2026) */}
      <div
        className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
        style={{
          background: `${accent}10`,
          border: `1px solid ${accent}30`,
        }}
        data-testid="analysis-complete-banner"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex items-center justify-center rounded-full shrink-0"
            style={{ width: 28, height: 28, background: accent, color: 'white' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="min-w-0">
            <div className="font-body truncate" style={{ fontSize: 13, fontWeight: 700, color: accent }}>
              Analysis complete
            </div>
            <div className="font-body truncate" style={{ fontSize: 11, color: 'var(--text-medium)' }}>
              Open the dedicated Results page for tables, figures, and downloads.
            </div>
          </div>
        </div>
        <button
          onClick={() => { window.location.href = `/results/${analysisId}`; }}
          className="flex items-center gap-1.5 font-body btn-press transition-all shrink-0"
          style={{
            fontSize: 13, fontWeight: 700, borderRadius: 'var(--radius-md)',
            padding: '8px 18px', border: `1px solid ${accent}`,
            color: 'white', background: accent, boxShadow: `0 2px 6px ${accent}40`,
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 10px ${accent}60`; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 2px 6px ${accent}40`; }}
          data-testid="view-full-results"
        >
          <ExternalLink size={13} /> View Full Results
        </button>
      </div>

      {/* Export + Paper integration row */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-body" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-light)', marginRight: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Export</span>
        {['docx', 'xlsx', 'pdf'].map(fmt => (
          <button
            key={fmt}
            onClick={() => onExport(analysisId, fmt)}
            disabled={!!exporting}
            className="flex items-center gap-1.5 font-body btn-press transition-colors disabled:opacity-50"
            style={{
              fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius-md)',
              padding: '5px 12px', border: '1px solid var(--border-color)',
              color: 'var(--text-medium)', background: 'white',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green-500)'; e.currentTarget.style.color = 'var(--green-700)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.color = 'var(--text-medium)'; }}
            data-testid={`export-${fmt}-button`}
          >
            {exporting === `${analysisId}-${fmt}` ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Download size={11} />
            )}
            {fmt.toUpperCase()}
          </button>
        ))}
        <button
          onClick={() => onConnectPaper && onConnectPaper(analysisId)}
          className="flex items-center gap-1.5 font-body btn-press transition-colors"
          style={{
            fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius-md)',
            padding: '5px 12px', border: '2px solid var(--green-900)',
            color: 'var(--green-900)', background: 'white',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--green-900)'; e.currentTarget.style.color = 'white'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = 'var(--green-900)'; }}
          data-testid="connect-paper-button"
        >
          <FileText size={11} /> Send to Write My Paper
        </button>
        {supportsDecisionCard && (
          <>
            <button
              onClick={handleDecisionCard}
              className="flex items-center gap-1.5 font-body btn-press transition-colors"
              style={{
                fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-md)',
                padding: '5px 12px', border: `2px solid ${accent}`,
                color: 'white', background: accent,
                boxShadow: `0 2px 4px ${accent}40`,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 4px 8px ${accent}60`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 2px 4px ${accent}40`; }}
              data-testid="decision-card-button"
              title="Breeder's Decision Card — single-page laminate-friendly PDF for the first trait (Sub-Batches 3I/3J/3K/3L/3M/3N/3O/3P)"
            >
              <Award size={11} /> Decision Card PDF
            </button>
            <button
              onClick={handleMultiTraitCard}
              className="flex items-center gap-1.5 font-body btn-press transition-colors"
              style={{
                fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-md)',
                padding: '5px 12px', border: `2px solid ${accent}`,
                color: accent, background: 'white',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = accent; }}
              data-testid="multi-trait-decision-card-button"
              title="Multi-Trait Decision Card — one page per trait in a single PDF (Sub-Batch 3Q)"
            >
              <Award size={11} /> Multi-Trait Card
            </button>
          </>
        )}
        {supportsPresentationDeck && (
          <button
            onClick={handlePresentationDeck}
            className="flex items-center gap-1.5 font-body btn-press transition-colors"
            style={{
              fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-md)',
              padding: '5px 12px', border: `2px solid ${accent}`,
              color: accent, background: 'white',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = accent; }}
            data-testid="presentation-deck-button"
            title="Presentation Deck — committee deck (10 slides single-trait / 1 slide-set per trait for multi-trait, automatic) with embedded ΔG bar chart and spatial-trend heatmap when applicable (Sub-Batches 3N + 3N+2)"
          >
            <FileText size={11} /> Presentation Deck (.pptx)
          </button>
        )}
      </div>
    </div>
  );
}

export default ExportBar;
